import type { Data } from '@bulknutrients/bulk-shared-lib/interfaces/ExplorePage';
import {
  getAllExploreSlugs,
  getExploreData,
  getExplorePreviewData,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/exploreData';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import { BlogCard } from '@components/Blog/BlogCard';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { useLocalStorage } from '@hooks/useLocalStorage';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { type ReactElement, useEffect } from 'react';

interface Props {
  pageData: Data;
  isPreview: boolean;
}

export default function ExplorePage({
  pageData,
  isPreview,
}: Props): ReactElement {
  const { setRecentlyViewedPage } = useLocalStorage();

  useEffect(() => {
    setRecentlyViewedPage({
      page: { slug: pageData.slug, title: pageData.title },
      viewedTime: Date.now(),
    });
  }, [pageData, setRecentlyViewedPage]);

  return (
    <>
      <Head>
        <title>{pageData.metaTitle}</title>
        <meta name="title" content={pageData.metaTitle} />
        <meta name="description" content={pageData.metaDescription} />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}${pageData.slug}`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Buy', path: '/buy' },
            { title: pageData.title, path: pageData.slug },
          ]}
        />
      </Container>
      <Container as="main" id="main-content">
        <div className="justify-self-center w-narrow-page">
          <h1>{pageData.title}</h1>
          <PortableTextBlockRenderer content={pageData.bodyText} />
        </div>
        <section className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {pageData.blogs.map((blog) => (
            <BlogCard key={blog.slug} blog={blog} />
          ))}
        </section>
      </Container>
    </>
  );
}

ExplorePage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getAllExploreSlugs();

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  preview,
  previewData,
}) => {
  try {
    let pageData: Data;
    if (preview) {
      // This is preview mode
      pageData = await getExplorePreviewData(
        (previewData as { id: string }).id
      );
    } else {
      pageData = await getExploreData(params?.slug?.toString() as string);
    }

    if (!pageData) {
      // try redirect
      const redirect = await getRedirect(
        `/explore/${params?.slug?.toString().split(',').join('/')}` as string
      );
      if (redirect) {
        return {
          redirect: {
            destination: redirect.newUrl,
            permanent: redirect.permanent,
          },
        };
      }

      // fallback failed
      return {
        notFound: true,
      };
    }

    return {
      props: {
        pageData,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.error(error);
    return {
      props: {
        pageData: {},
      },
    };
  }
};
